import { AlertCircle, AlertTriangle } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import type { InternalApiKey, InternalApiKeyAllowedIp } from "../types/internalApiKeys.types";
import { sameIps } from "../utils/internalApiKeyForm";
import { InternalApiKeyIpsEditor } from "./InternalApiKeyIpsEditor";

export interface InternalApiKeyIpsDialogProps {
  apiKey: InternalApiKey;
  /** Resolves true once saved, false when the person backed out of the verification code. */
  onSubmit: (ips: InternalApiKeyAllowedIp[]) => Promise<boolean>;
  onClose: () => void;
}

export function InternalApiKeyIpsDialog({ apiKey, onSubmit, onClose }: InternalApiKeyIpsDialogProps) {
  const { t } = useTranslations("internalApiKeys");
  const [ips, setIps] = useState<InternalApiKeyAllowedIp[]>(apiKey.allowed_ips);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const changed = !sameIps(apiKey.allowed_ips, ips);
  const turnsOff = ips.length === 0 && apiKey.is_active && !apiKey.allow_low_risk_without_ip;
  const onlyCardholders = ips.length === 0 && apiKey.allow_low_risk_without_ip;

  const save = async () => {
    setSubmitError(null);
    setIsSaving(true);
    try {
      if (await onSubmit(ips)) onClose();
    } catch (error: unknown) {
      setSubmitError(HttpApiService.getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ColocaDialog
      open
      onOpenChange={(open) => !open && !isSaving && onClose()}
      title={t("ips_dialog_title", { name: apiKey.name })}
      description={t("ips_hint")}
      footer={
        <>
          <ColocaButton variant="ghost" onClick={onClose} disabled={isSaving}>
            {t("cancel")}
          </ColocaButton>
          <ColocaButton variant="primary" onClick={() => void save()} loading={isSaving} disabled={!changed}>
            {t("ips_dialog_submit")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-4">
        <InternalApiKeyIpsEditor ips={ips} onChange={setIps} disabled={isSaving} />
        {turnsOff && <ColocaAlert variant="warning" icon={AlertTriangle} text={t("ips_dialog_turns_off")} />}
        {onlyCardholders && <ColocaAlert variant="secondary" text={t("ips_dialog_only_cardholders")} />}
        {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}
      </div>
    </ColocaDialog>
  );
}
